import get from "lodash-es/get.js";
import { type RefObject, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router";

export function useNavigationScroll(mainRef: RefObject<HTMLElement | null>) {
  const location = useLocation();
  // This is set if a new slideover is opened by `useModalNavigation`
  const isLocationModal = get(location.state, "isModal");

  // Remember scroll positions for up to 10 historical locations
  const scrollPositionsRef = useRef<
    { locationKey: string; scrollTop: number }[]
  >([]);

  const wasLocationModalRef = useRef(false);
  const locationPathnameBeforeModalRef = useRef<string | undefined>(undefined);
  // Hack: make this available to the effect without triggering a re-render
  const locationPathnameRef = useRef(location.pathname);
  locationPathnameRef.current = location.pathname;

  // Save and restore <main>'s scroll position across navigations; React
  // Router's ScrollRestoration only handles window scroll. Relies on <main>
  // being the only page-level scroller (see `PageLayout`).
  useLayoutEffect(() => {
    const mainElement = mainRef.current;
    if (!mainElement) {
      return;
    }

    // This is set if a slideover is currently open
    const isNavigationInModal =
      mainElement.ownerDocument.querySelector(
        "[data-ar-slideover]:not([data-exiting])",
      ) !== null;

    // Don't do anything if we're opening a modal or navigating inside a modal 😬
    if (isLocationModal || isNavigationInModal) {
      // Remember this for when the modal closes
      wasLocationModalRef.current = true;
      return;
    }

    // Hack: if we just left a modal and we're on the same path as before the modal, don't restore the scroll position.
    const shouldSkipRestoreScroll =
      wasLocationModalRef.current &&
      locationPathnameRef.current === locationPathnameBeforeModalRef.current;
    wasLocationModalRef.current = false;
    locationPathnameBeforeModalRef.current = location.pathname;

    // Find or create a scroll position object for this location
    const existingScrollPosition = scrollPositionsRef.current.find(
      (s) => s.locationKey === location.key,
    );
    const scrollPosition = existingScrollPosition ?? {
      locationKey: location.key,
      scrollTop: 0,
    };

    // Keep only the 10 most recent scroll positions to avoid growing this array indefinitely
    scrollPositionsRef.current = [
      ...scrollPositionsRef.current.filter(
        (s) => s.locationKey !== location.key,
      ),
      scrollPosition,
    ].slice(-10);

    // Restore the scroll position
    const scrollTopToRestore = scrollPosition.scrollTop;
    const restoreScroll = () => {
      if (shouldSkipRestoreScroll) {
        return;
      }

      mainElement.scrollTo({
        top: scrollTopToRestore,
        behavior: "instant",
      });
    };

    let restoreScrollTimeout: ReturnType<typeof setTimeout> | undefined;

    // If the scroll position is not (yet) within the scrollable range, restore the scroll position after a render cycle. Data may load async and React Aria collections may require a double render.
    const maxScrollTop = mainElement.scrollHeight - mainElement.clientHeight;
    if (Math.floor(scrollTopToRestore) <= maxScrollTop) {
      restoreScroll();
    } else {
      restoreScrollTimeout = setTimeout(restoreScroll, 1);
    }

    const handleScroll = () => {
      // Update the scroll position so we can restore it later
      scrollPosition.scrollTop = mainElement.scrollTop;
    };

    mainElement.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (restoreScrollTimeout) {
        clearTimeout(restoreScrollTimeout);
      }

      mainElement.removeEventListener("scroll", handleScroll);
    };
  }, [mainRef, location.key, isLocationModal]);
}
